import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Card, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { Plus, UsersThree } from "@phosphor-icons/react";
import { deleteAccount, getUsers } from "../../../config/apisauce";
import { ADMIN_ROLE, GENERIC_COLUMN_PROPS, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { ACTIVE, INACTIVE, STATUS_COLORS } from "../../../enums/statusEnums";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import useStore from "../../../states/store";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { formatDate } from "../../../helpers/dateHelper";
import useNotiStack from "../../../hooks/useNotiStack";
import { DeleteAccountDialog } from "../../shared/DeleteAccountDialog";
import { usePage } from "@inertiajs/react";
import { useLogoutUser } from "../../../components/generic/functions/genericFunctions";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import { CreateOrUpdateUserForm } from "./CreateOrUpdateUserForm";

const UsersPage = () => {
  const { auth } = usePage().props;
  const handleLogout = useLogoutUser();
  const { success } = useNotiStack();
  const [, { openLoader, closeLoader }] = useStore();
  const [users, setUsers] = useState({});
  const [userPagination, setUserPagination] = useState({ pageSize: 10, page: 0 });
  const [userId, setUserId] = useState(null);
  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [openRemoveUserConfirmationDialog, setOpenRemoveUserConfirmationDialog] = useState(false);

  const fetchUsers = async () => {
    openLoader();
    const params = {
      ...INITIAL_GET_PARAMS,
      role: ADMIN_ROLE,
      status: [INACTIVE, ACTIVE],
      per_page: userPagination?.pageSize,
      page: userPagination?.page + 1,
      paginated: 1,
    };
    const response = await getUsers(params);
    if (response.ok) {
      setUsers(response?.data);
    }
    closeLoader();
  };

  const handleOpenAddEditDialog = (values, isAdd = true) => {
    setOpenAddEditDialog(true);
    setSelectedUser({ data: values, isAdd: isAdd });
  };

  const handleOpenRemoveUserDialog = (id) => {
    setUserId(id);
    setOpenRemoveUserConfirmationDialog(true);
  };

  const handleDeleteAccount = async () => {
    openLoader();
    const response = await deleteAccount({ id: userId });
    if (response.ok) {
      success('Account deleted!');
      setOpenRemoveUserConfirmationDialog(false);
      fetchUsers();
      if (userId === auth?.user?.id) handleLogout();
    }
    closeLoader();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1.5} mt={0.5}>
            <GenericAvatar location={row?.details?.profile_picture?.location} avatarId={row?.id} size={'42px'} backgroundColor={'background.default'} />
            <Typography fontWeight={700}>{row?.name}</Typography>
          </Stack>
        )
      },
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      renderCell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row?.roles[0]?.name}</span>,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography mt={1.7} fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[row?.status]}>{row?.status}</Typography>
        )
      },
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} gap={1} mt={1.1}>
            <Button variant="outlined" fullWidth onClick={() => handleOpenAddEditDialog(row, false)}>Edit</Button>
            <Button variant="contained" fullWidth color="error" onClick={() => handleOpenRemoveUserDialog(row?.id)}>Remove User</Button>
          </Stack>
        )
      },
      ...GENERIC_COLUMN_PROPS
    }
  ];

  useEffect(() => {
    fetchUsers();
  }, [userPagination]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={1.5}>Users</Typography>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', padding: 2 }}>
        <Grid container justifyContent={'space-between'}>
          <Grid item>
            <Typography variant="h6" color="primary" fontWeight={700}>User List</Typography>
          </Grid>
          <Grid item>
            <Button sx={{ width: 150 }} startIcon={<Plus size={16} weight="bold" />} onClick={() => handleOpenAddEditDialog()}>Add New User</Button>
          </Grid>
        </Grid>
        <Typography mb={2} fontWeight={500}>View and manage all users.</Typography>
        {
          users?.data?.length > 0 ? <GenericMuiDatagrid
            {...users}
            columns={columns}
            setPagination={setUserPagination}
          >
            {
              users?.data?.map((user, index) => {
                return (
                  <Fragment key={`user-key-${index}`}>
                    <Box sx={{ padding: '16px' }}>
                      <Grid container>
                        <Grid item size={4}>
                          <GenericAvatar location={user?.details?.profile_picture?.location} size={'92px'} backgroundColor={'background.default'} />
                        </Grid>
                        <Grid item size={8}>
                          <Stack direction={'row'} justifyContent={'space-between'}>
                            <Typography fontWeight={700} noWrap width={{ xs: 140, md: '100%' }}>{user?.name}</Typography>
                            <Typography color="text.light">{formatDate(new Date(user?.created_at), DAY_ONLY_FORMAT)}</Typography>
                          </Stack>
                          <Stack>
                            <Typography noWrap>{user?.details?.profession ?? 'None'}</Typography>
                            <Typography fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[user?.status]}>{user?.status}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                      <Stack direction={'row'} gap={1} mt={1}>
                        <Button variant="outlined" fullWidth onClick={() => handleOpenAddEditDialog(user, false)}>Edit</Button>
                        <Button variant="contained" fullWidth color="error" onClick={() => handleOpenRemoveUserDialog(user?.id)}>Remove User</Button>
                      </Stack>
                    </Box>
                    {index !== (users?.data?.length - 1) && <Divider sx={{ borderColor: 'borders.main' }} />}
                  </Fragment>
                )
              })
            }
          </GenericMuiDatagrid> : <GenericNoData height={{ md: '64vh', lg: '69vh', xl: '75vh' }} icon={<UsersThree size={40} />} label={'No users'} />
        }
      </Card>
      <DeleteAccountDialog
        openDeleteAccountConfirmationDialog={openRemoveUserConfirmationDialog}
        setOpenDeleteAccountConfirmationDialog={setOpenRemoveUserConfirmationDialog}
        handleDeleteAccount={() => handleDeleteAccount()}
      />
      <GenericDialog
        title={selectedUser?.isAdd ? 'Add new user' : 'Edit user'}
        width={'855px'}
        openDialog={openAddEditDialog}
        handleClose={() => setOpenAddEditDialog(false)}
      >
        <Box sx={{ overflowX: 'auto', paddingRight: { xs: 1, md: 1, lg: 0 }, minHeight: { lg: '60vh' } }}>
          <CreateOrUpdateUserForm
            selectedUser={selectedUser}
            setOpenAddEditDialog={setOpenAddEditDialog}
            role={auth?.user?.role}
            handleCloseAddEditDialog={() => setOpenAddEditDialog(false)}
            setUsers={setUsers}
          />
        </Box>
      </GenericDialog>
    </Box >
  );
};

export default UsersPage;